import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "./firebase";
import {
    doc,
    setDoc,
    serverTimestamp,
    getDoc,
    deleteDoc,
    collection,
    onSnapshot,
    query,
    orderBy,
    where,
    updateDoc,
    arrayUnion,
    writeBatch,
} from "firebase/firestore";

import JoinerStatus from "./components/JoinerStatus";
import JoinerPendingMatch from "./components/JoinerPendingMatch";
import JoinerMatchHistory from "./components/JoinerMatchHistory";
import JoinerMatchRequests from "./components/player/JoinerMatchRequests";
import JoinerCreateMatchRequest from "./components/player/JoinerCreateMatchRequest";

import { useAuth } from "./context/AuthContext";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import Swal from "sweetalert2";
import { THEME_COLOR, LEVELS, APP_CLUB_ID, CLUB_NAME } from "./config/club";
import { Html5QrcodeScanner } from "html5-qrcode";
import QRLanding from "./components/player/QRLanding";
import AppHeader from "./components/layout/AppHeader";
import AuthLanding from "./components/player/AuthLanding";
import { requestPushPermission } from "./firebase-messaging";
import { savePushToken } from "./utils/savePushToken";
import SessionList from "./components/player/SessionList";
import { useCourtTTSAlert } from "./components/useCourtTTSAlert";
import BottomNav from "./components/layout/BottomNav";
import GlobalAdsModal from "./components/ads/GlobalAdsModal"
/* ================= TOAST ================= */
const Toast = Swal.mixin({
    toast: true,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
});
const ToastShow = (type, message) =>
    Toast.fire({ icon: type, title: message });

export default function JoinClub() {
    const [params] = useSearchParams();
    const clubId = params.get("club") || APP_CLUB_ID;
    const sessionId = params.get("session");

    const THEME_VIOLET = THEME_COLOR;
    const { user, loading, loginGoogle, loginGuest, logout, mode, setAppMode } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [name, setName] = useState("");
    const [dupr, setDupr] = useState("");
    const [level, setLevel] = useState(1);
    const [submitted, setSubmitted] = useState(false);
    const [loadings, setLoading] = useState(false);
    const [alertEnabled, setAlertEnabled] = useState(false);
    const [sessionValid, setSessionValid] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [qrRedirecting, setQrRedirecting] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const [loggingIn, setLoggingIn] = useState(false);
    const [ready, setReady] = useState(false);
    const [activeJoinedSessions, setActiveJoinedSessions] = useState([]);
    const [pastJoinedSessions, setPastJoinedSessions] = useState([]);
    const [pendingMatches, setPendingMatches] = useState([]);

    // idle and offline detect
    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [listenerStuck, setListenerStuck] = useState(false);
    const [reconnecting, setReconnecting] = useState(false);
    const [listenerKey, setListenerKey] = useState(0);
    const [showReconnectButton, setShowReconnectButton] = useState(false);

    /* ================= FROM JOINERSTATUS ================= */
    const [hasAcknowledged, setHasAcknowledged] = useState(false);
    const [player, setPlayer] = useState(null);
    const [queue, setQueue] = useState([]);
    const [activeMatch, setActiveMatch] = useState(null);
    const [gamesPlayed, setGamesPlayed] = useState(0);
    const uid = user?.uid;

    /* ================= LOAD PLAYER STATE ================= */
    useEffect(() => {
        if (!user || !clubId || !sessionId || !sessionValid) return;

        const init = async () => {
            const queueSnap = await getDoc(
                doc(db, "clubs", clubId, "sessions", sessionId, "queue", user.uid)
            );
            setSubmitted(queueSnap.exists());

            const userSnap = await getDoc(doc(db, "users", user.uid));
            if (userSnap.exists()) {
                setName(userSnap.data().displayName || "");
                setDupr(userSnap.data().dupr || "");
                setLevel(userSnap.data().level || 1);
            }
            setReady(true);
        };

        init();
    }, [user, clubId, sessionId, sessionValid]);

    //Watchdog timer for idles or dc
    useEffect(() => {
        const interval = setInterval(() => {
            const diff = Date.now() - lastUpdate;

            if (diff > (60000 * 5)) { // 5 min idle or not receiving anything
                setListenerStuck(true);
            } else {
                setListenerStuck(false);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [lastUpdate]);
    useEffect(() => {
        if (listenerStuck) {
            setShowReconnectButton(false);

            const timer = setTimeout(() => {
                setShowReconnectButton(true);
            }, 15000);

            return () => clearTimeout(timer);
        }
    }, [listenerStuck]);

    // Reset acknowledgement logic: If match ends or status changes, allow alert for next time
    useEffect(() => {
        if (player?.status !== "playing") {
            setHasAcknowledged(false);
        }
    }, [player?.status]);
    // Subscriptions

    const shouldListenMatches =
        ["waiting", "pending", "playing"].includes(player?.status);

    const shouldListenQueue =
        ["waiting", "pending"].includes(player?.status);

    useEffect(() => {
        if (!uid || !clubId || !sessionId || !shouldListenMatches) return;

        const q = query(
            collection(db, "clubs", clubId, "sessions", sessionId, "matches"),
            where("status", "==", "playing")
        );

        return onSnapshot(q, (snap) => {
            const match = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .find(m => m.players?.some(p => p.id === uid));
            setActiveMatch(match || null);
            setLastUpdate(Date.now());
        });
    }, [clubId, uid, sessionId, shouldListenMatches]);

    useEffect(() => {
        if (!uid || !clubId || !sessionId || !shouldListenMatches) return;

        const q = query(
            collection(db, "clubs", clubId, "sessions", sessionId, "matches"),
            where("status", "==", "finished")
        );

        return onSnapshot(q, snap => {
            const count = snap.docs.filter(d =>
                d.data().players?.some(p => p.id === uid)
            ).length;
            setGamesPlayed(count);
            setLastUpdate(Date.now());
        });
    }, [clubId, uid, sessionId, shouldListenMatches]);

    useEffect(() => {
        if (!uid || !clubId || !sessionId) return;
        return onSnapshot(doc(db, "clubs", clubId, "sessions", sessionId, "players", uid), (snap) => {
            if (snap.exists()) setPlayer(snap.data());
            setLastUpdate(Date.now());
        });
    }, [clubId, uid, sessionId]);

    useEffect(() => {
        if (!clubId || !sessionId || !shouldListenQueue) return;

        const q = query(
            collection(db, "clubs", clubId, "sessions", sessionId, "queue"),
            orderBy("waitingSince", "asc")
        );

        return onSnapshot(q, (snap) => {
            setQueue(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLastUpdate(Date.now());
        });
    }, [clubId, sessionId, shouldListenQueue]);

    useEffect(() => {
        if (!user?.uid) return;

        const initPush = async () => {
            const token = await requestPushPermission();
            if (!token) return;

            await savePushToken({
                uid: user.uid,
                token,
            });
        };

        initPush();
    }, [user?.uid]);

    useEffect(() => {
        if (!user?.uid || !clubId) return;

        const q = query(
            collection(db, "clubs", clubId, "sessions"),
            where("playerUids", "array-contains", user.uid),
            where("status", "==", "active")
        );

        return onSnapshot(q, (snap) => {
            setActiveJoinedSessions(
                snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            );
            setLastUpdate(Date.now());
        });
    }, [user?.uid, clubId]);

    // useEffect(() => {
    //     if (!user?.uid || !clubId) return;

    //     const q = query(
    //         collection(db, "clubs", clubId, "sessions"),
    //         where("playerUids", "array-contains", user.uid),
    //         where("status", "==", "ended")
    //     );

    //     return onSnapshot(q, (snap) => {
    //         setPastJoinedSessions(
    //             snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    //         );
    //     });
    // }, [user?.uid, clubId]);


    // Stop Alert Hook
    const stopAlert2 = useCourtTTSAlert({
        active: player?.status === "playing" && !hasAcknowledged,
        court: activeMatch?.court,
    });

    useEffect(() => {
        if (loading) return;
        if (user?.role === "queue_master" && !["admin", "player"].includes(mode) && user.clubs?.includes(APP_CLUB_ID)) {
            navigate(`/select-mode${location.search}`, { replace: true });
        }
    }, [user, mode, loading, navigate, location.search]);

    /* ================= SESSION VALIDATION ================= */
    useEffect(() => {
        if (!clubId || !sessionId) {
            setSessionValid(false);
            return;
        }

        const checkSession = async () => {
            const snap = await getDoc(
                doc(db, "clubs", clubId, "sessions", sessionId)
            );
            setSessionValid(
                snap.exists() && snap.data().status === "active"
            );
        };

        checkSession();
    }, [clubId, sessionId]);

    /* ================= ALERT PREF ================= */
    useEffect(() => {
        const saved = localStorage.getItem("alertsEnabled");
        if (saved === "true") setAlertEnabled(true);
    }, []);

    useEffect(() => {
        if (alertEnabled) localStorage.setItem("alertsEnabled", "true");
    }, [alertEnabled]);


    /* ================= SCANNER ================= */
    useEffect(() => {
        if (!scanning) return;

        const scanner = new Html5QrcodeScanner(
            "qr-reader",
            { fps: 10, qrbox: 250 },
            false
        );

        scanner.render(
            (decodedText) => {
                scanner.clear();
                setScanning(false);
                if (!decodedText.includes("join") || !decodedText.includes("club=")) {
                    ToastShow("error", "Invalid QR code");
                    return;
                }
                setQrRedirecting(true);
                // 🔥 Redirect to scanned URL
                setTimeout(() => {
                    window.location.href = decodedText;
                }, 300);
            },
            (error) => {
                console.log(error)
            }
        );

        return () => {
            scanner.clear().catch(() => { });
        };
    }, [scanning]);

    /* ================= PENDING MATCHES ================= */
    //console.log(submitted);
    useEffect(() => {
        if (!clubId || !sessionId || !uid) return;
        if (!["pending", "waiting", "playing"].includes(player?.status)) return;

        const q = query(
            collection(db, "clubs", clubId, "sessions", sessionId, "matches"),
            where("status", "==", "pending"),
            orderBy("createdAt")
        );

        return onSnapshot(q, (snap) => {
            setPendingMatches(
                snap.docs.map(d => ({ id: d.id, ...d.data() }))
            );
            setLastUpdate(Date.now());
        });
    }, [clubId, sessionId, uid, player?.status]);


    const pendingOrder = pendingMatches.findIndex(match =>
        match.players?.some(p => p.id === uid)
    ) + 1;

    /* ================= JOIN QUEUE ================= */
    const joinQueue = async () => {
        if (submitted || loadings) return;
        if (!name.trim()) return ToastShow("error", "Enter your name");
        // if (!dupr.trim()) return ToastShow("error", "Enter your DUPR ID");
        if (!sessionValid) return ToastShow("error", "Session ended");
        if (!user) return ToastShow("error", "Auth error");

        setLoading(true);

        try {
            const uid = user.uid;
            const now = Date.now();

            const username = user.username || null;
            const photoLink = user.photoURL || null;

            const userRef = doc(db, "users", uid);
            const playerRef = doc(db, "clubs", clubId, "sessions", sessionId, "players", uid);
            const queueRef = doc(db, "clubs", clubId, "sessions", sessionId, "queue", uid);
            const sessionRef = doc(db, "clubs", clubId, "sessions", sessionId);

            // 🔒 Double-join guard
            const existing = await getDoc(queueRef);
            if (existing.exists()) {
                setSubmitted(true);
                setLoading(false);
                return;
            }

            const batch = writeBatch(db);

            batch.set(
                userRef,
                {
                    displayName: name.trim(),
                    dupr: dupr.trim(),
                    level,
                    username,
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );

            batch.set(
                playerRef,
                {
                    uid,
                    photoURL: photoLink,
                    username,
                    name: name.trim(),
                    level,
                    status: "waiting",
                    waitingSince: now,
                    joinedAt: serverTimestamp(),
                },
                { merge: true }
            );

            batch.set(queueRef, {
                playerId: uid,
                photoURL: photoLink,
                username,
                name: name.trim(),
                level,
                waitingSince: now,
                joinedAt: serverTimestamp(),
            });

            batch.update(sessionRef, {
                playerUids: arrayUnion(uid),
            });

            await batch.commit();

            setSubmitted(true);
        } catch (e) {
            console.error(e);
            ToastShow("error", "Failed to join queue");
        } finally {
            setLoading(false);
        }
    };


    /* ================= LOGOUT ================= */
    const handleLogout = async () => {
        const result = await Swal.fire({
            title: "Logout?",
            html: "Leave the queue and logout?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
            confirmButtonColor: "#dc3545",
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        // START LOADER
        setLoggingOut(true);

        try {
            if (clubId && sessionId && user?.uid) {
                // Remove from queue first
                // await deleteDoc(
                //     doc(db, "clubs", clubId, "sessions", sessionId, "queue", user.uid)
                // );
                await updateDoc(doc(db, "clubs", clubId, "sessions", sessionId, "players", user.uid), { status: "left", leftAt: serverTimestamp() });
            }
            await logout();
            // Navigation usually happens automatically via AuthContext/Protected Routes
        } catch (err) {
            console.error(err);
            setLoggingOut(false); // Hide loader if there's an error so they can try again
            Swal.fire("Error", "Failed to logout properly", "error");
        } finally {
            setLoggingOut(false);
        }
    };

    const restartListeners = () => {
        setReconnecting(true);

        setTimeout(() => {
            setListenerKey(prev => prev + 1);
            setListenerStuck(false);
            setLastUpdate(Date.now());
            setReconnecting(false);
        }, 1500);
    };

    /* ================= LOADING / INVALID ================= */
    if (loading || sessionValid === null) {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: "var(--app-bg)", opacity: 0.9 }}>
                <div className="spinner-border" style={{ color: THEME_VIOLET }} />
            </div>
        );
    }
    if (qrRedirecting) {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-white d-flex flex-column align-items-center justify-content-center">
                <div
                    className="spinner-border mb-3"
                    style={{ color: THEME_VIOLET }}
                />
                <div className="fw-bold">Joining session…</div>
            </div>
        );
    }

    // /* ================= ADMIN REDIRECT ================= */
    if (user?.role === "queue_master" && mode === "admin" && user.clubs?.includes(APP_CLUB_ID)) {
        setAppMode(null);
        return <Navigate to="/select-mode" replace />;
    }

    if (
        !sessionValid &&
        user &&
        !(user?.role === "queue_master" && mode === "admin" && user.clubs?.includes(APP_CLUB_ID))
    ) {
        return (
            <>
                <AppHeader
                    user={user}
                    subtitle="Live Queue"
                    clubId={clubId}
                    sessionId={sessionId}
                    onLogout={handleLogout}
                    logout={logout}
                />
                <div className="min-vh-100 pb-5">
                    <GlobalAdsModal
                        clubId={clubId}
                        themeColor={THEME_VIOLET}
                        sessionKey={`${clubId}_${sessionId}`}
                    />
                    {!activeJoinedSessions.length > 0 && (
                        <QRLanding user={user} scanning={scanning} setScanning={setScanning} themeColor={THEME_VIOLET} />
                    )}
                    {activeJoinedSessions.length > 0 && (
                        <div className="container mt-4">
                            <div
                                className="mx-auto p-3 p-md-4"
                                style={{
                                    maxWidth: "500px",
                                    borderRadius: "24px",
                                    backgroundColor: "var(--card-bg)",
                                    border: "1px solid var(--border-color)",
                                    boxShadow: "0 10px 25px rgba(0,0,0,0.05)"
                                }}
                            >
                                {/* activeJoinedSessions Header Logic */}
                                <div className="mb-4">
                                    <SessionList
                                        title={
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                <span
                                                    className="fw-bold"
                                                    style={{
                                                        fontSize: "1rem",
                                                        color: "var(--text-color)"
                                                    }}
                                                >
                                                    Active Sessions
                                                </span>
                                            </div>
                                        }
                                        sessions={activeJoinedSessions}
                                        clubId={clubId}
                                    />
                                </div>

                                {/* Empty State Logic */}
                                {activeJoinedSessions.length === 0 && (
                                    <div className="text-center py-5">
                                        <div className="display-6 opacity-25">🥒🏓</div>
                                        <p
                                            className="small mt-2"
                                            style={{ color: "var(--bs-secondary-color)" }}
                                        >
                                            No club activity found yet.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <BottomNav />

            </>

        );
    }

    if (
        !sessionValid ||
        !user
    ) {
        return (
            <>
                <AuthLanding
                    themeColor={THEME_VIOLET}
                    loggingIn={loggingIn}
                    onGuestLogin={async () => {
                        setLoggingIn(true);
                        try {
                            await loginGuest();
                        } catch (e) {
                            console.error(e);
                            setLoggingIn(false);
                            ToastShow("error", "Guest login failed");
                        }
                    }}
                    onGoogleLogin={async () => {
                        setLoggingIn(true);
                        try {
                            await loginGoogle();
                        } catch (e) {
                            ToastShow("error", "Login failed. Please try again.");
                        } finally {
                            setLoggingIn(false);
                        }
                    }}
                />
                <GlobalAdsModal
                    clubId={clubId}
                    themeColor={THEME_VIOLET}
                    sessionKey={`${clubId}_${sessionId}`}
                />
            </>
        );
    }
    //console.log(user)

    if (submitted || player?.status === "playing" || player?.status === "pending" || player?.status === "waiting" || player?.status === "removed") {
        return (
            <div className="min-vh-100 pb-5" style={{ backgroundColor: "var(--app-bg)" }}>
                {!alertEnabled && (
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            zIndex: 2000,
                            backgroundColor: "rgba(0,0,0,0.65)",
                            backdropFilter: "blur(6px)"
                        }}
                    >
                        <div
                            className="rounded-4 shadow-lg p-4 w-100 mx-3 text-center"
                            style={{
                                maxWidth: 420,
                                background: "var(--card-bg)",
                                color: "var(--text-color)",
                                border: "1px solid var(--border-color)"
                            }}
                        >
                            <div className="fs-1 mb-3">🔔</div>

                            <h4 className="fw-bold mb-2">
                                Enable Court Alerts
                            </h4>

                            <p className="text-muted mb-4">
                                Sound alerts are required so you don’t miss your court call.
                            </p>

                            <button
                                className="btn btn-lg w-100 fw-bold text-white rounded-pill"
                                style={{ backgroundColor: THEME_VIOLET }}
                                onClick={() => {
                                    const u = new SpeechSynthesisUtterance("Alerts enabled");
                                    window.speechSynthesis.speak(u);
                                    if (navigator.vibrate) navigator.vibrate(50);
                                    setAlertEnabled(true);
                                }}
                            >
                                Enable Alerts
                            </button>
                        </div>
                    </div>
                )}

                {/* SLIM VIOLET HEADER */}
                <AppHeader
                    user={user}
                    subtitle="Live Queue"
                    clubId={clubId}
                    sessionId={sessionId}
                    onLogout={handleLogout}
                    logout={logout}
                />
                <GlobalAdsModal
                    clubId={clubId}
                    themeColor={THEME_VIOLET}
                    sessionKey={`${clubId}_${sessionId}`}
                />
                {reconnecting && (
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                            zIndex: 3000,
                            backdropFilter: "blur(10px)",
                            background: "color-mix(in srgb, var(--card-bg) 55%, transparent)"
                        }}
                    >
                        <div
                            className="text-center px-4 py-4 rounded-4 shadow-lg"
                            style={{
                                background: "var(--card-bg)",
                                border: "1px solid var(--border-color)",
                                minWidth: 240
                            }}
                        >
                            {/* Animated Status Dot */}
                            <div className="d-flex justify-content-center mb-3">
                                <div
                                    className="reconnect-dot"
                                    style={{
                                        background: THEME_COLOR
                                    }}
                                />
                            </div>

                            {/* Spinner */}
                            <div
                                className="spinner-border mb-2"
                                style={{
                                    color: THEME_COLOR,
                                    width: "2rem",
                                    height: "2rem"
                                }}
                            />

                            <div
                                className="fw-bold"
                                style={{
                                    color: "var(--text-color)",
                                    fontSize: "0.95rem"
                                }}
                            >
                                Reconnecting…
                            </div>

                            <div
                                className="small mt-1"
                                style={{
                                    color: "var(--muted-text)"
                                }}
                            >
                                Restoring live queue connection
                            </div>
                        </div>

                        <style>{`
      .reconnect-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        animation: reconnectPulse 1.6s infinite;
      }

      @keyframes reconnectPulse {
        0% {
          transform: scale(0.8);
          opacity: 0.7;
        }
        50% {
          transform: scale(1.4);
          opacity: 0.3;
        }
        100% {
          transform: scale(0.8);
          opacity: 0.7;
        }
      }
    `}</style>
                    </div>
                )}

                <div key={listenerKey} className="container" style={{ maxWidth: 450 }}>
                    {listenerStuck && (
                        <>
                            <hr />

                            <div
                                className="d-flex justify-content-between align-items-center mb-3 px-3 py-2 rounded-3"
                                style={{
                                    background: "color-mix(in srgb, #f59e0b 18%, var(--card-bg))",
                                    border: "1px solid color-mix(in srgb, #f59e0b 45%, var(--border-color))"
                                }}
                            >
                                <div
                                    className="d-flex align-items-center gap-2 small fw-semibold"
                                    style={{ color: "var(--text-color)" }}
                                >
                                    <i className="bi bi-wifi-off" style={{ color: "#f59e0b" }} />
                                    Connection seems lost{!showReconnectButton && '. Trying to reconnect...'}
                                </div>

                                {showReconnectButton && (
                                    <button
                                        onClick={restartListeners}
                                        className="btn btn-sm rounded-pill px-3"
                                        style={{
                                            background: "var(--surface-bg)",
                                            border: "1px solid var(--border-color)",
                                            color: "var(--text-color)",
                                            fontWeight: 600
                                        }}
                                    >
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Reconnect
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    <JoinerPendingMatch setLastUpdate={setLastUpdate} clubId={clubId} sessionId={sessionId} playerId={user?.uid} alertEnabled={alertEnabled} pendingOrder={pendingOrder} />
                    <JoinerCreateMatchRequest
                        clubId={clubId}
                        sessionId={sessionId}
                        uid={user?.uid}
                        queue={queue}
                        player={player}
                    />
                    <JoinerMatchRequests
                        clubId={clubId}
                        sessionId={sessionId}
                        uid={user?.uid}
                        players={queue}
                    />
                    <JoinerStatus pendingOrder={pendingOrder} clubId={clubId} sessionId={sessionId} hasAcknowledged={hasAcknowledged} setHasAcknowledged={setHasAcknowledged} player={player} setPlayer={setPlayer} queue={queue} setQueue={setQueue} activeMatch={activeMatch} setActiveMatch={setActiveMatch} gamesPlayed={gamesPlayed} setGamesPlayed={setGamesPlayed} user={user} THEME_VIOLET={THEME_VIOLET} stopAlert2={stopAlert2} />
                    <div className="my-5">
                        <JoinerMatchHistory setLastUpdate={setLastUpdate} clubId={clubId} sessionId={sessionId} uid={user?.uid} />
                    </div>
                </div>
                <BottomNav />
            </div>
        );
    }
    if (!ready && user) {
        return (
            <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: "var(--app-bg)", opacity: 0.9 }}>
                <div className="spinner-border" style={{ color: THEME_VIOLET }} />
            </div>
        );
    }

    return (
        <>
            <div className="container mt-5 px-4" style={{ maxWidth: 450 }}>
                {loadings && (
                    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 9999, backgroundColor: "var(--app-bg)", opacity: 0.9 }}>
                        <div className="spinner-border" style={{ color: THEME_VIOLET }} />
                    </div>
                )}

                <div className="text-center mb-4">
                    <img src="/img/rrc2.png" alt="Logo" className="img-fluid rounded-circle shadow mb-4" style={{ width: "120px", height: "120px", objectFit: 'cover', border: "4px solid var(--card-bg)" }} />
                    <h1 className="fw-black" style={{ color: THEME_VIOLET, fontSize: '2rem' }}>{CLUB_NAME}</h1>
                    <p className="text-muted small text-uppercase tracking-widest">Smarter match-making</p>
                </div>

                <div
                    className="card shadow-lg rounded-4 p-4"
                    style={{
                        backgroundColor: "var(--card-bg)",
                        border: "1px solid var(--border-color)"
                    }}
                >
                    <label className="form-label fw-bold small text-muted text-uppercase">DUPR Display Name</label>
                    <input
                        className="form-control form-control-lg mb-4 border-0"
                        style={{
                            backgroundColor: "var(--surface-bg)",
                            color: "var(--text-color)"
                        }}
                        placeholder="Enter your name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={loadings}
                    />
                    <label className="form-label fw-bold small text-muted text-uppercase">DUPR ID</label>
                    <input
                        className="form-control form-control-lg mb-4 border-0"
                        style={{
                            backgroundColor: "var(--surface-bg)",
                            color: "var(--text-color)"
                        }}
                        placeholder="Enter your DUPR ID (Optional)"
                        value={dupr}
                        onChange={(e) => setDupr(e.target.value)}
                        disabled={loadings}
                    />
                    <label className="form-label fw-bold small text-muted text-uppercase">Skill Level</label>
                    <div className="row g-2 mb-4">
                        {LEVELS.map(l => (
                            <div key={l.value} className="col-6">
                                <button
                                    className={`btn w-100 py-2 small fw-bold rounded-3 ${level === l.value ? "text-white shadow" : ""
                                        }`}
                                    style={{
                                        backgroundColor:
                                            level === l.value
                                                ? THEME_VIOLET
                                                : "var(--surface-bg)",
                                        border:
                                            level === l.value
                                                ? `1px solid ${THEME_VIOLET}`
                                                : "1px solid var(--border-color)",
                                        color:
                                            level === l.value
                                                ? "#fff"
                                                : "var(--text-color)"
                                    }}
                                    onClick={() => setLevel(l.value)}
                                    type="button"
                                >
                                    {l.label.split('–')[1] || l.label}
                                </button>
                            </div>
                        ))}
                    </div>

                    <button
                        className="btn btn-lg w-100 fw-bold text-white rounded-pill shadow-sm py-3"
                        style={{ backgroundColor: THEME_VIOLET }}
                        onClick={joinQueue}
                        disabled={loadings}
                    >
                        {loadings ? "Joining..." : "Join the Queue"}
                    </button>
                </div>
            </div>
        </>
    );
}